/* ═══════════════════════════════════════════════════════
   HVAC/R REFERENCE DATA
   Refrigerant properties, P-T charts, line sizing
   ═══════════════════════════════════════════════════════ */

// ── Pressure-Temperature Data (saturated, PSIG) ──
// Common refrigerants: R-410A, R-22, R-134a, R-404A, R-407C
export interface RefrigerantPT {
  label: string;
  data: { tempF: number; psig: number }[];
}

export const REFRIGERANTS: RefrigerantPT[] = [
  {
    label: "R-410A",
    data: [
      { tempF: -20, psig: 20.7 }, { tempF: -10, psig: 29.0 }, { tempF: 0, psig: 38.7 },
      { tempF: 10, psig: 50.0 }, { tempF: 20, psig: 63.1 }, { tempF: 25, psig: 70.3 },
      { tempF: 30, psig: 78.1 }, { tempF: 35, psig: 86.5 }, { tempF: 40, psig: 95.6 },
      { tempF: 45, psig: 105.4 }, { tempF: 50, psig: 115.9 }, { tempF: 55, psig: 127.3 },
      { tempF: 60, psig: 139.5 }, { tempF: 65, psig: 152.6 }, { tempF: 70, psig: 166.7 },
      { tempF: 75, psig: 181.7 }, { tempF: 80, psig: 197.8 }, { tempF: 85, psig: 215.0 },
      { tempF: 90, psig: 233.3 }, { tempF: 95, psig: 252.9 }, { tempF: 100, psig: 273.7 },
      { tempF: 105, psig: 295.9 }, { tempF: 110, psig: 319.5 }, { tempF: 115, psig: 344.7 },
      { tempF: 120, psig: 371.4 }, { tempF: 125, psig: 399.9 }, { tempF: 130, psig: 430.1 },
    ],
  },
  {
    label: "R-22",
    data: [
      { tempF: -20, psig: 0.5 }, { tempF: -10, psig: 5.5 }, { tempF: 0, psig: 11.8 },
      { tempF: 10, psig: 19.5 }, { tempF: 20, psig: 28.5 }, { tempF: 25, psig: 33.5 },
      { tempF: 30, psig: 39.0 }, { tempF: 35, psig: 44.9 }, { tempF: 40, psig: 51.4 },
      { tempF: 45, psig: 58.4 }, { tempF: 50, psig: 65.9 }, { tempF: 55, psig: 74.0 },
      { tempF: 60, psig: 82.7 }, { tempF: 65, psig: 92.0 }, { tempF: 70, psig: 101.9 },
      { tempF: 75, psig: 112.5 }, { tempF: 80, psig: 123.8 }, { tempF: 85, psig: 135.8 },
      { tempF: 90, psig: 148.6 }, { tempF: 95, psig: 162.2 }, { tempF: 100, psig: 176.6 },
      { tempF: 105, psig: 191.8 }, { tempF: 110, psig: 208.0 }, { tempF: 115, psig: 225.1 },
      { tempF: 120, psig: 243.1 }, { tempF: 125, psig: 262.2 }, { tempF: 130, psig: 282.3 },
    ],
  },
  {
    label: "R-134a",
    data: [
      { tempF: -20, psig: -6.7 }, { tempF: -10, psig: -2.4 }, { tempF: 0, psig: 2.9 },
      { tempF: 10, psig: 9.2 }, { tempF: 20, psig: 16.7 }, { tempF: 25, psig: 20.8 },
      { tempF: 30, psig: 25.4 }, { tempF: 35, psig: 30.4 }, { tempF: 40, psig: 35.8 },
      { tempF: 45, psig: 41.7 }, { tempF: 50, psig: 48.2 }, { tempF: 55, psig: 55.2 },
      { tempF: 60, psig: 62.7 }, { tempF: 65, psig: 70.9 }, { tempF: 70, psig: 79.7 },
      { tempF: 75, psig: 89.2 }, { tempF: 80, psig: 99.4 }, { tempF: 85, psig: 110.3 },
      { tempF: 90, psig: 122.0 }, { tempF: 95, psig: 134.5 }, { tempF: 100, psig: 147.8 },
      { tempF: 105, psig: 162.1 }, { tempF: 110, psig: 177.2 }, { tempF: 115, psig: 193.3 },
      { tempF: 120, psig: 210.5 },
    ],
  },
  {
    label: "R-404A",
    data: [
      { tempF: -20, psig: 6.2 }, { tempF: -10, psig: 13.7 }, { tempF: 0, psig: 22.7 },
      { tempF: 10, psig: 33.3 }, { tempF: 20, psig: 45.7 }, { tempF: 25, psig: 52.6 },
      { tempF: 30, psig: 60.2 }, { tempF: 35, psig: 68.4 }, { tempF: 40, psig: 77.3 },
      { tempF: 45, psig: 87.0 }, { tempF: 50, psig: 97.4 }, { tempF: 55, psig: 108.7 },
      { tempF: 60, psig: 120.8 }, { tempF: 65, psig: 133.8 }, { tempF: 70, psig: 147.8 },
      { tempF: 75, psig: 162.8 }, { tempF: 80, psig: 178.8 }, { tempF: 85, psig: 195.9 },
      { tempF: 90, psig: 214.2 }, { tempF: 95, psig: 233.7 }, { tempF: 100, psig: 254.5 },
      { tempF: 105, psig: 276.7 }, { tempF: 110, psig: 300.2 },
    ],
  },
  {
    label: "R-407C",
    data: [
      { tempF: -20, psig: -0.2 }, { tempF: -10, psig: 6.7 }, { tempF: 0, psig: 14.9 },
      { tempF: 10, psig: 24.5 }, { tempF: 20, psig: 35.7 }, { tempF: 25, psig: 41.9 },
      { tempF: 30, psig: 48.6 }, { tempF: 35, psig: 55.9 }, { tempF: 40, psig: 63.9 },
      { tempF: 45, psig: 72.5 }, { tempF: 50, psig: 81.8 }, { tempF: 55, psig: 91.9 },
      { tempF: 60, psig: 102.7 }, { tempF: 65, psig: 114.4 }, { tempF: 70, psig: 126.9 },
      { tempF: 75, psig: 140.3 }, { tempF: 80, psig: 154.7 }, { tempF: 85, psig: 170.0 },
      { tempF: 90, psig: 186.4 }, { tempF: 95, psig: 203.8 }, { tempF: 100, psig: 222.4 },
      { tempF: 105, psig: 242.2 }, { tempF: 110, psig: 263.2 },
    ],
  },
];

// ── Interpolate saturated temp from pressure ──
export const satTempFromPressure = (ref: RefrigerantPT, psig: number): number | null => {
  const d = ref.data;
  if (psig <= d[0].psig) return d[0].tempF;
  if (psig >= d[d.length - 1].psig) return d[d.length - 1].tempF;
  for (let i = 0; i < d.length - 1; i++) {
    if (psig >= d[i].psig && psig <= d[i + 1].psig) {
      const ratio = (psig - d[i].psig) / (d[i + 1].psig - d[i].psig);
      return d[i].tempF + ratio * (d[i + 1].tempF - d[i].tempF);
    }
  }
  return null;
};

// ── Interpolate pressure from temp ──
export const pressureFromTemp = (ref: RefrigerantPT, tempF: number): number | null => {
  const d = ref.data;
  if (tempF <= d[0].tempF) return d[0].psig;
  if (tempF >= d[d.length - 1].tempF) return d[d.length - 1].psig;
  for (let i = 0; i < d.length - 1; i++) {
    if (tempF >= d[i].tempF && tempF <= d[i + 1].tempF) {
      const ratio = (tempF - d[i].tempF) / (d[i + 1].tempF - d[i].tempF);
      return d[i].psig + ratio * (d[i + 1].psig - d[i].psig);
    }
  }
  return null;
};

// ── P-T Chart table format ──
export const ptChartHeaders = ["Temp (°F)", "R-410A", "R-22", "R-134a", "R-404A", "R-407C"];
export const ptChartRows: (string | number)[][] = (() => {
  const temps = [-20, -10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130];
  return temps.map(t => {
    const row: (string | number)[] = [`${t}°F`];
    for (const ref of REFRIGERANTS) {
      const entry = ref.data.find(d => d.tempF === t);
      row.push(entry ? entry.psig : "—");
    }
    return row;
  });
})();

// ── Refrigerant Line Sizing (copper, tons capacity) ──
// Suction, Liquid, and Hot Gas line sizes per capacity (tons)
export const lineSizingHeaders = ["Tons", "Suction", "Liquid", "Hot Gas"];

export interface LineSizeEntry {
  tons: number;
  suction: string;
  liquid: string;
  hotGas: string;
}

// R-410A line sizing (most common modern refrigerant)
export const R410A_LINE_SIZES: LineSizeEntry[] = [
  { tons: 1.5, suction: "3/4\"", liquid: "3/8\"", hotGas: "1/2\"" },
  { tons: 2,   suction: "3/4\"", liquid: "3/8\"", hotGas: "5/8\"" },
  { tons: 2.5, suction: "7/8\"", liquid: "3/8\"", hotGas: "5/8\"" },
  { tons: 3,   suction: "7/8\"", liquid: "3/8\"", hotGas: "5/8\"" },
  { tons: 3.5, suction: "7/8\"", liquid: "3/8\"", hotGas: "3/4\"" },
  { tons: 4,   suction: "1-1/8\"", liquid: "3/8\"", hotGas: "3/4\"" },
  { tons: 5,   suction: "1-1/8\"", liquid: "3/8\"", hotGas: "7/8\"" },
  { tons: 7.5, suction: "1-3/8\"", liquid: "1/2\"", hotGas: "7/8\"" },
  { tons: 10,  suction: "1-5/8\"", liquid: "5/8\"", hotGas: "1-1/8\"" },
  { tons: 12.5,suction: "1-5/8\"", liquid: "5/8\"", hotGas: "1-1/8\"" },
  { tons: 15,  suction: "2-1/8\"", liquid: "3/4\"", hotGas: "1-3/8\"" },
  { tons: 20,  suction: "2-1/8\"", liquid: "7/8\"", hotGas: "1-3/8\"" },
  { tons: 25,  suction: "2-5/8\"", liquid: "7/8\"", hotGas: "1-5/8\"" },
  { tons: 30,  suction: "2-5/8\"", liquid: "1-1/8\"", hotGas: "1-5/8\"" },
  { tons: 40,  suction: "3-1/8\"", liquid: "1-1/8\"", hotGas: "2-1/8\"" },
  { tons: 50,  suction: "3-5/8\"", liquid: "1-3/8\"", hotGas: "2-1/8\"" },
];

export const lineSizingRows: (string | number)[][] = R410A_LINE_SIZES.map(l => [
  l.tons, l.suction, l.liquid, l.hotGas,
]);

// ── Charge Estimation ──
// Approximate oz/ft of refrigerant per line size (liquid line)
export const CHARGE_PER_FOOT: { size: string; ozPerFt: number }[] = [
  { size: "1/4\"", ozPerFt: 0.19 },
  { size: "3/8\"", ozPerFt: 0.43 },
  { size: "1/2\"", ozPerFt: 0.78 },
  { size: "5/8\"", ozPerFt: 1.22 },
  { size: "3/4\"", ozPerFt: 1.76 },
  { size: "7/8\"", ozPerFt: 2.40 },
  { size: "1-1/8\"", ozPerFt: 3.97 },
  { size: "1-3/8\"", ozPerFt: 5.95 },
];

// Factory charge base (typical oz per ton)
export const FACTORY_CHARGE_OZ_PER_TON = 40;
